import type { ComponentType, SVGProps } from 'react';
import { BarChart3, Bookmark, ChartLine, History, Play, Trophy } from 'lucide-react';

import { MyBoardsPanel } from '@/components/myBoards/MyBoardsPanel';
import { QgammonLeaderboardPanel } from '@/components/hub/panels/QgammonLeaderboardPanel';
import { QgammonMatchesPanel } from '@/components/hub/panels/QgammonMatchesPanel';
import { QgammonRankedPanel } from '@/components/hub/panels/QgammonRankedPanel';
import { QgammonStatsPanel } from '@/components/hub/panels/QgammonStatsPanel';
import { LiveBroadcastIcon, SitngoBoltIcon } from '@/components/hub/HubIcons';
import { QgammonLivePanel } from '@/components/hub/panels/QgammonLivePanel';
import { SitngoBackgammonPanel } from '@/components/hub/panels/SitngoBackgammonPanel';
import { env } from '@/env';
import { QgammonTournamentsPanel } from '@/components/hub/panels/QgammonTournamentsPanel';
import { sitngoBackgammonAcceso } from '@/lib/limulo/sitngoAcceso';
import { myBoardsAcceso } from '@/lib/backgammon/myBoardsAcceso';

/**
 * ⛔ Niente `'play'` dal 01/10 (I-282): la scheda «Gioca» di prima non c'e' piu', e la chiave fuori dal
 * tipo fa fallire la compilazione di chi provasse a riaprirla. La prima scheda e' `'ranked'`.
 */
export type BackgammonSectionKey =
  | 'ranked'
  | 'tournaments'
  | 'sitngo'
  | 'rankings'
  | 'matches'
  | 'myBoards'
  | 'stats'
  | 'live';

export interface BackgammonSectionDef {
  key: BackgammonSectionKey;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
  Panel: ComponentType;
  /** Il pannello porta il proprio scorrevole: v. `SectionDef.ownScroll` in `HubSections`. */
  ownScroll: boolean;
}

/**
 * LE SCHEDE DEL BACKGAMMON — **UN SOLO ELENCO, montato in due posti**: la rotta `/backgammon`
 * (`routes/backgammon/BackgammonHome.tsx`) e la Hub con il backgammon attivo
 * (`components/hub/HubSections.tsx`).
 *
 * Luca, 23/09: «4 uguali alla hub». Il modo di tenerle uguali NON e' scrivere due elenchi e
 * sperare che restino allineati — e' che l'elenco esista una volta. Come la lobby, che e' un
 * solo componente montato in due posti. ⇒ Chi aggiunge una scheda la aggiunge QUI e la trova
 * in entrambe; chi la aggiunge in uno solo dei due ha rifatto il difetto che questo file
 * toglie (`tools/verify-lobby-bg.ts` lo vieta).
 *
 * Ordine: Gioca · Tornei (· Sit&Go) · Classifica · Partite (· My Boards) · Statistiche (· Live).
 * «My Boards» dal 04/10 (Luca, I-343/I-346; ADR-0191): dopo «Partite», solo col predicato `myBoardsAcceso` — flag
 * `VITE_FEATURE_BACKGAMMON_MY_BOARDS_ENABLED`, SPENTO in ogni file env per tutto il T0. Con Sit&Go e Live le schede sono 8
 * (la griglia a 4 colonne di `BackgammonSectionTabs` va a capo in due righe): e' l'OTTAVA, il TETTO — con 9 servirebbero tre
 * righe e a 320 px l'anello di «Gioca» non regge piu' (misura di tma-orcb, 04/10). Dopo, ogni scheda nuova costa una scelta.
 * L'etichetta, `hub:sections.myBoards`, e' «My Boards» in tutte e 4 le lingue (e' il nome del prodotto): misurata a 320 px,
 * 61,3 su 74 px (10 px maiuscolo, colonna da 82 px), contro i 66,8 di «Statistiche».
 * «Sit&Go» dal 04/10 (Luca, I-334 punto 4; I-340): solo col predicato `sitngoBackgammonAcceso` — flag
 * `VITE_FEATURE_TOURNAMENT_SITNGO_BACKGAMMON_ENABLED`, acceso solo su staging. Come «Live», il default legge il
 * predicato; l'etichetta e l'icona sono quelle della Hub CR (`hub:sections.sitngo`, `SitngoBoltIcon`).
 * 🔴 **Dal 01/10 «Gioca» E' la vecchia «Ranked»** — Luca, I-282: «Sezione ranked la chiami sezione Play
 * e la sezione play di oggi la nascondi perché le funzionalità sono già state raccolte nella nuova
 * ranked (che diventa play)». Quindi la prima scheda ha la CHIAVE `'ranked'` e l'etichetta/icona di
 * «Gioca». ⚠️ La chiave non si rinomina: le chiavi delle schede della Hub sono condivise fra i giochi
 * (`HubSections`, la barra di `AzionePersistente`), e rinominarla qui le toccherebbe tutte.
 * ~~«Gioca» come scheda a se' (la lobby: bot, amico, codice, tornei)~~: superata da I-282. Il suo
 * pannello (`QgammonPlayPanel` → `BackgammonLobby`) non e' montato da nessuna parte; i file restano su
 * disco finche' il proprietario della TMA non decide (`tools/verify-lobby-bg.ts` vieta di rimontarli).
 * La striscia «Tornei backgammon» che stava in fondo alla lobby se ne va con lei: i tornei hanno la
 * loro scheda.
 * «Ranked» dal 24/09: scelta C di Luca (Hub backgammon a 8 schede, KB
 * `2026-09-24-backgammon-hub-proposta.md`), la scheda con le code classificate che prima stavano in
 * «Gioca» — ed e' quella che dal 01/10 si chiama «Gioca». «Statistiche» dal 26/09 (variante s3, Luca
 * 25/09), al posto che la griglia della C le da': seconda riga, dopo «Partite». Le altre due schede
 * della C (Live, Missioni) arrivano ciascuna con la sua immagine approvata.
 * «Live» dal 26/09 (Luca I-208, impianto (a) approvato con L-2298): dopo Statistiche, SOLO con
 * `live` vero — `VITE_FEATURE_BACKGAMMON_LIVE_ENABLED`, acceso solo su staging. Il default legge il
 * flag, cosi' i due posti che montano l'elenco lo ricevono uguale senza doverlo passare.
 * Copy: le chiavi `hub:sections.*` (`sections.stats` nuova, con Statistiche; `sections.live` c'era gia').
 *
 * ⚠️ **«Tornei» esiste solo se `tournaments` e' vero**, cioe' con `VITE_FEATURE_LIMULO_ENABLED`
 * acceso: i tornei sono della sezione Limulo, e una scheda che porta a un'area che il flag ha
 * spento e' un vicolo cieco (la regola dei pannelli in `HubSections`: una scheda che puo' non
 * avere un pannello non si offre). Con il flag spento le schede sono tre, e va bene: le
 * serrature non si allentano per far tornare il conto. Il ruolo `beta` non si ricontrolla qui:
 * chi arriva a queste schede l'ha gia' — `RoleGate` sulla rotta, `showQgammon` sulla Hub — e
 * Limulo chiede lo stesso.
 */
export function backgammonSectionDefs(
  t: (key: string, options: { defaultValue: string }) => string,
  {
    tournaments,
    live = env.featureBackgammonLiveEnabled,
    sitngo = sitngoBackgammonAcceso(),
    myBoards = myBoardsAcceso(),
  }: { tournaments: boolean; live?: boolean; sitngo?: boolean; myBoards?: boolean },
): BackgammonSectionDef[] {
  const list: BackgammonSectionDef[] = [
    {
      // ⚠️ Chiave `'ranked'`, etichetta «Gioca» (I-282): v. il blocco qui sopra.
      key: 'ranked',
      Icon: Play,
      label: t('sections.play', { defaultValue: 'Play' }),
      Panel: QgammonRankedPanel,
      ownScroll: true,
    },
  ];
  if (tournaments) {
    list.push({
      key: 'tournaments',
      Icon: Trophy,
      label: t('sections.tournaments', { defaultValue: 'Tournaments' }),
      Panel: QgammonTournamentsPanel,
      ownScroll: true,
    });
  }
  // «Sit&Go» dopo «Tornei», solo a predicato acceso (`sitngoBackgammonAcceso`: flag suo, code, Limulo, backgammon).
  // Il default legge il predicato, cosi' Hub e `/backgammon` ricevono l'elenco uguale senza doverlo passare.
  if (sitngo) {
    list.push({
      key: 'sitngo',
      Icon: SitngoBoltIcon,
      label: t('sections.sitngo', { defaultValue: 'Sit&Go' }),
      Panel: SitngoBackgammonPanel,
      ownScroll: true,
    });
  }
  list.push(
    {
      key: 'rankings',
      Icon: BarChart3,
      label: t('sections.rankings', { defaultValue: 'Rankings' }),
      Panel: QgammonLeaderboardPanel,
      // `true` dal 24/09: la lista scorre DENTRO il pannello e la riga «tu» resta fissata sotto.
      ownScroll: true,
    },
    {
      key: 'matches',
      Icon: History,
      label: t('sections.matches', { defaultValue: 'Matches' }),
      Panel: QgammonMatchesPanel,
      ownScroll: true,
    },
  );
  // «My Boards» dopo «Partite», solo a predicato acceso (`myBoardsAcceso`: flag suo e backgammon). Il default legge il
  // predicato, cosi' Hub e `/backgammon` ricevono l'elenco uguale senza doverlo passare.
  if (myBoards) {
    list.push({
      key: 'myBoards',
      Icon: Bookmark,
      label: t('sections.myBoards', { defaultValue: 'My Boards' }),
      Panel: MyBoardsPanel,
      ownScroll: true,
    });
  }
  list.push({
    key: 'stats',
    Icon: ChartLine,
    label: t('sections.stats', { defaultValue: 'Stats' }),
    Panel: QgammonStatsPanel,
    ownScroll: true,
  });
  if (live) {
    list.push({
      key: 'live',
      Icon: LiveBroadcastIcon,
      label: t('sections.live', { defaultValue: 'Live' }),
      Panel: QgammonLivePanel,
      ownScroll: true,
    });
  }
  return list;
}
